import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import { SubscriptionPlan, SubscriptionProductFeature } from '@domain/subscription/models/SubscriptionPlan';
import { Check, Close } from '@mui/icons-material';

interface SubscriptionCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan?: boolean;
  onSelect?: (plan: SubscriptionPlan) => void;
  disabled?: boolean;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  plan,
  isCurrentPlan = false,
  onSelect,
  disabled = false,
}) => {
  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`;
  };


  const getButtonText = () => {
    if (isCurrentPlan) return 'Current Plan';
    return 'Select Plan';
  };

  const getFeatureText = (feature: SubscriptionProductFeature) => {
    switch (feature.limitValue) {
      case -1:
        return `unlimited ${feature.name}`;
      case null:
        return feature.name;
      default:
        return `up to ${feature.limitValue} ${feature.name}`;
    }
  };

  return (

    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: isCurrentPlan ? 'primary.main' : undefined,
        borderWidth: isCurrentPlan ? 2 : 1,
        cursor: disabled ? 'default' : 'pointer',
        position: 'relative',
        overflow: 'visible',
        opacity: disabled ? 0.6 : 1,
        '&:hover': {
          boxShadow: disabled ? undefined : 3,
        },
      }}
    >
      {isCurrentPlan && (
        <Chip
          label="Current Plan"
          color="primary"
          size="small"
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            zIndex: 1,
          }}
        />
      )}

      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="column" spacing={1} mb={1}>
          <Typography variant="h6" fontWeight={600}>
            {plan.name}
          </Typography>


          <Typography variant="body2" color="text.secondary" mb={2}>
            {plan.description}
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h4" color="primary">
              {formatPrice(plan.baseFeeInCents)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              per month
            </Typography>

            <Typography variant="body2" color="text.secondary">
              + {formatPrice(plan.feePerOrderInCents)} per order
            </Typography>

          </Box>

        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1} flexGrow={1}>
          <Typography variant="body2" color="text.secondary">What is included:</Typography>

          {plan.features?.sort((a, b) => a.sortOrder - b.sortOrder).map(feature => (
            <Stack direction="row" alignItems="center" spacing={1} key={feature.id}>
              {feature.isEnabled ? <Check color="primary" /> : <Close color="error" />}
              <Typography variant="body2" color="text.secondary">
                {getFeatureText(feature)}
              </Typography>
            </Stack>
          ))}


        </Stack>


      </CardContent>
      {onSelect && (
        <CardActions sx={{ p: 2, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => onSelect?.(plan)}
            disabled={disabled}
            color="primary"
          >
            {getButtonText()}
          </Button>
        </CardActions>
      )}
    </Card>
  );
};
